import React, { useMemo, useState } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'

import { Types, reportDevices as actionRD, reportStoreNotes as actionRSN } from '../../actions/reports'
import { Button, Headings, Input, Padding } from '../../components'
import { colors, Routes } from '../../constants'
import { getSelectedEstateId } from '../../reducers/app'
import { DateUtils } from '../../utils'

import DatesRange from './DatesRange'
import MigrationStatus from './MigrationStatus'
import ReportTypes, { defaultReport, Translations } from './Reports.types'
import { usePageTitle } from '../../actions/app'

const today = new Date()
today.setHours(0)
today.setMinutes(0)
today.setMinutes(0)
today.setMilliseconds(0)
const initialFrom = DateUtils.minusDays(today, 30)
const initialTo = today.getTime()

function downloadBlob(blob, name) {
    const downloadTag = document.createElement('a')
    downloadTag.href = URL.createObjectURL(blob)
    downloadTag.download = name
    downloadTag.click()
}

function Reports({
    history,
    match: {
        params: { report = defaultReport },
    },
    reportDevices,
    reportStoreNotes,
    selectedEstate,
    t,
}) {
    usePageTitle(t('reports'))

    const [migrationStatus, setMigrationStatus] = useState(['1', '0'])
    const [from, setFrom] = useState(initialFrom)
    const [to, setTo] = useState(initialTo)
    const reportTypeSelectValues = useMemo(
        () =>
            Object.keys(ReportTypes).map((key) => ({
                name: Translations[key],
                value: ReportTypes[key],
            })),
        []
    )
    const onReportTypeChange = ({ target: { value } }) => {
        const url = Routes.buildRoute(Routes.REPORTS, {
            estate: selectedEstate,
            report: value,
        })
        history.replace(url)
    }

    const onSubmit = async () => {
        switch (report) {
            case ReportTypes.DEVICES: {
                const { type, payload } = await reportDevices({
                    migrationStatus: migrationStatus[0],
                    withMigrationStatus: migrationStatus.length === 1,
                })

                if (type !== Types.REPORT_DEVICES.success) {
                    return null
                }

                downloadBlob(payload, 'report_devices.csv')
                break
            }
            case ReportTypes.STORE_NOTES: {
                const { type, payload } = await reportStoreNotes(from, to)

                if (type !== Types.REPORT_STORE_NOTES.success) {
                    return null
                }

                downloadBlob(payload, 'report_store_notes.csv')
                break
            }
            default:
        }
    }

    const includesMigratedState = report === ReportTypes.DEVICES
    const includesDatesRange = report === ReportTypes.STORE_NOTES

    return (
        <Padding color={colors.greylightest} fullHeight>
            <Padding color="white" style={{ boxShadow: '0 0 3px #0000001c' }}>
                <Padding top right left>
                    <Headings.H2>{t('reports')}</Headings.H2>
                </Padding>
                <Padding>
                    <Padding bottom>
                        <Headings.H2>{t('reports-type')}</Headings.H2>
                    </Padding>
                    <Input type="multiple" onChange={onReportTypeChange} onlyOne value={[report]} values={reportTypeSelectValues} />
                </Padding>
                {includesMigratedState && <MigrationStatus migrationStatus={migrationStatus} setMigrationStatus={setMigrationStatus} />}
                {includesDatesRange && <DatesRange from={from} setFrom={setFrom} setTo={setTo} to={to} />}
                <Padding>
                    <Button onClick={onSubmit}>{t('reports-generate')}</Button>
                </Padding>
            </Padding>
        </Padding>
    )
}

Reports.propTypes = {
    history: PropTypes.shape({
        replace: PropTypes.func,
    }).isRequired,
    match: PropTypes.shape({
        params: PropTypes.shape({
            report: PropTypes.oneOf(Object.values(ReportTypes)),
        }).isRequired,
    }).isRequired,
    reportDevices: PropTypes.func.isRequired,
    reportStoreNotes: PropTypes.func.isRequired,
    selectedEstate: PropTypes.string.isRequired,
    t: PropTypes.func.isRequired,
}

Reports.defaultProps = {}

function mapStateToProps(state) {
    return {
        selectedEstate: getSelectedEstateId(state),
    }
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            reportDevices: actionRD,
            reportStoreNotes: actionRSN,
        },
        dispatch
    )
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Reports))
